import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Form,
  Button,
  Card,
  Container,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import type IProgramacao from "../../interfaces/IProgramacao";
import CallbackApiService from "../../services/CallbackApiService.tsx";
import logoUnimed from "../../assets/images/logo-unimed.png";

export default function PaginaInscricaoEventoPublica() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [programacao, setProgramacao] = useState<IProgramacao | null>(null);
  const [tempoRestanteMs, setTempoRestanteMs] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [errors, setErrors] = useState<{
    cpf?: string;
    email?: string;
  }>({});

  const [form, setForm] = useState({
    nome: "",
    email: "",
    cpf: "",
  });

  function obterLocalizacao(): Promise<{
    latitude: number;
    longitude: number;
  }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("Geolocalização não é suportada neste dispositivo.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          let msg = "Não foi possível obter a localização.";
          if (error.code === error.PERMISSION_DENIED)
            msg = "Permissão de localização negada.";
          else if (error.code === error.POSITION_UNAVAILABLE)
            msg = "Sinal de GPS indisponível.";
          else if (error.code === error.TIMEOUT)
            msg = "Tempo esgotado ao buscar localização.";
          reject(msg);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
        },
      );
    });
  }

  function formatarDuracao(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const dias = Math.floor(totalSeconds / 86400);
    const horas = Math.floor((totalSeconds % 86400) / 3600);
    const minutos = Math.floor((totalSeconds % 3600) / 60);
    const segundos = totalSeconds % 60;
    const partes = [];
    if (dias > 0) partes.push(`${dias}d`);
    if (horas > 0 || dias > 0) partes.push(`${horas}h`);
    partes.push(`${minutos.toString().padStart(2, "0")}m`);
    partes.push(`${segundos.toString().padStart(2, "0")}s`);
    return partes.join(" ");
  }

  function formatarDataHora(data: Date): string {
    return data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function maskCpf(value: string): string {
    return value
      .replace(/\D/g, "")
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }

  function isValidCpf(cpf: string): boolean {
    const cleanCpf = cpf.replace(/\D/g, "");
    if (cleanCpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cleanCpf)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += Number(cleanCpf[i]) * (10 - i);
    let firstDigit = (sum * 10) % 11;
    if (firstDigit === 10) firstDigit = 0;
    if (firstDigit !== Number(cleanCpf[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += Number(cleanCpf[i]) * (11 - i);
    let secondDigit = (sum * 10) % 11;
    if (secondDigit === 10) secondDigit = 0;
    return secondDigit === Number(cleanCpf[10]);
  }

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  useEffect(() => {
    if (!token) {
      setErro("Token inválido ou não informado.");
      setLoading(false);
      return;
    }

    async function validarProgramacao() {
      try {
        const response =
          await CallbackApiService.buscarProgramacaoPorTokenQr(token);
        const prog: IProgramacao = response;

        const agora = new Date();
        const dataAbertura = new Date(prog.dataAbertura as string);
        const dataFim = new Date(prog.dataFim as string);

        if (agora < dataAbertura) {
          const msRestante = dataAbertura.getTime() - agora.getTime();
          setTempoRestanteMs(msRestante);
          setErro(`A confirmação de presença ainda não está liberada.`);
          setLoading(false);
          return;
        }

        if (agora > dataFim) {
          setErro(
            `O prazo para confirmação de presença foi encerrado.\n\nEncerrado em ${formatarDataHora(dataFim)}.`,
          );
          return;
        }

        setProgramacao(prog);
      } catch (e) {
        setErro(
          "Não foi possível localizar a programação do evento. Tente novamente ou procure a organização do evento.",
        );
      } finally {
        setLoading(false);
      }
    }

    validarProgramacao();
  }, [token]);

  useEffect(() => {
    if (tempoRestanteMs === null) return;
    if (tempoRestanteMs <= 0) {
      window.location.reload();
      return;
    }
    const interval = setInterval(() => {
      setTempoRestanteMs((prev) => {
        if (prev === null) return null;
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [tempoRestanteMs]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    if (name === "cpf") {
      setForm((prev) => ({ ...prev, cpf: maskCpf(value) }));
      setErrors((prev) => ({ ...prev, cpf: undefined }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (isSubmitting || !programacao) return;

    const newErrors: typeof errors = {};
    if (!isValidEmail(form.email)) newErrors.email = "E-mail inválido.";
    if (!isValidCpf(form.cpf)) newErrors.cpf = "CPF inválido.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (programacao.solicitarLocalizacaoUsuario === true) {
      setShowLocationConfirm(true);
    } else {
      await enviarConfirmacao(null, null);
    }
  }

  async function enviarConfirmacao(latitude: number | null, longitude: number | null) {
    if (!programacao) return;

    const payload = {
      programacaoId: programacao.id,
      eventoId: programacao.eventoId,
      qrCode: programacao.tokenQr,
      nome: form.nome,
      cpf: form.cpf.replace(/\D/g, ""),
      email: form.email,
      latitude, 
      longitude, 
    };

    try {
      setIsSubmitting(true);
      await CallbackApiService.confirmarPresencaAutoatendimento(payload);
      setSuccessMessage("Presença confirmada com sucesso!");
      setLocationError(null);
      setShowLocationConfirm(false);
      setForm({ nome: "", email: "", cpf: "" }); 
    } catch (error: any) {
      const apiMessage = error?.message || error?.response?.data?.message || "Erro ao confirmar presença. Tente novamente.";
      setSuccessMessage(null);
      setLocationError(apiMessage);
      setShowLocationConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (erro) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#ffffff",
        }}
      >
        <Container className="p-0 m-0 d-flex justify-content-center">
          <Card style={{ width: "90%", maxWidth: 420 }}>
            <Card.Body>
              <div
                className="mb-3"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <img
                  src={logoUnimed}
                  className="img-logo-unimed"
                  alt="Logo Unimed"
                />
              </div>
              <Alert variant="warning" className="text-center mb-0">
                <p className="mb-2">{erro}</p>
                {tempoRestanteMs !== null && (
                  <strong>
                    Liberação em {formatarDuracao(tempoRestanteMs)}
                  </strong>
                )}
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#fff",
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container className="p-0 m-0 d-flex justify-content-center">
        <Card style={{ width: "90%", maxWidth: 420 }}>
          <Card.Body>
            <div
              className="div-logo-unimed text-center mb-3"
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <img
                src={logoUnimed}
                className="img-logo-unimed"
                alt="Logo Unimed"
              />
            </div>

            <Card.Title className="text-center mb-2">
              Confirmação de Presença
            </Card.Title>
            <Card.Subtitle className="text-center mb-4 text-muted">
              {programacao?.atividade}
            </Card.Subtitle>

            {successMessage && (
              <Alert variant="success" className="mb-3">
                {successMessage}
              </Alert>
            )}
            {locationError && (
              <Alert variant="warning" className="mb-3">
                {locationError}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  placeholder="Digite seu nome completo"
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>E-mail</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  placeholder="Digite seu e-mail"
                  required
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>CPF</Form.Label>
                <Form.Control
                  type="text"
                  name="cpf"
                  value={form.cpf}
                  onChange={handleChange}
                  isInvalid={!!errors.cpf}
                  placeholder="000.000.000-00"
                  required
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cpf}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-grid">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ backgroundColor: "#00995d", borderColor: "#00995d" }}
                >
                  {isSubmitting ? "Enviando..." : "Confirmar Presença"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      <Modal
        show={showLocationConfirm}
        centered
        backdrop="static"
        keyboard={!isSubmitting}
      >
        <Modal.Header>
          <Modal.Title>Confirmação de Localização</Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center">
          {!isSubmitting ? (
            <>
              <p>
                A localização é necessária para confirmação de proximidade do
                evento.
              </p>
              <p>Deseja permitir o uso da sua localização para continuar?</p>
              {locationError && (
                <Alert variant="danger" className="mt-2">
                  {locationError}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Spinner animation="border" className="mb-3" />
              <p className="mb-0">Confirmando presença, por favor aguarde...</p>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {!isSubmitting && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLocationConfirm(false);
                  setLocationError(
                    "Não é possível confirmar presença sem autorização de localização.",
                  );
                }}
              >
                Não permitir
              </Button>

              <Button
                variant="success"
                onClick={async () => {
                  try {
                    setLocationError(null);
                    setIsSubmitting(true);

                    const { latitude, longitude } = await obterLocalizacao();
                    await enviarConfirmacao(latitude, longitude);
                  } catch (err: any) {
                    setIsSubmitting(false);
                    setLocationError(
                      typeof err === "string"
                        ? err
                        : "Erro ao obter localização.",
                    );
                  }
                }}
              >
                Permitir localização
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
